/**
 * Как давно человек был в Lens — общие правила для списка пользователей
 * и карточки: подпись «3 дня назад» и цвет точки рядом с ней.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** «только что», «5 минут назад», «вчера», «12 дней назад», «3 месяца назад». */
export function formatAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'никогда';
  const diff = Math.max(0, now - new Date(iso).getTime());
  if (diff < MINUTE) return 'только что';
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `${m} ${plural(m, 'минуту', 'минуты', 'минут')} назад`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `${h} ${plural(h, 'час', 'часа', 'часов')} назад`;
  }
  const d = Math.floor(diff / DAY);
  if (d === 1) return 'вчера';
  if (d < 31) return `${d} ${plural(d, 'день', 'дня', 'дней')} назад`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo} ${plural(mo, 'месяц', 'месяца', 'месяцев')} назад`;
  const y = Math.floor(d / 365);
  return `${y} ${plural(y, 'год', 'года', 'лет')} назад`;
}

/** Полная дата для подсказок: «21 сентября 2026, 14:05». */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** «21 сент. 2026» — для плиток, где мало места. */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export type Presence = 'online' | 'recent' | 'idle' | 'gone' | 'never';

/**
 * online — был в последние 15 минут; recent — за неделю;
 * idle — за месяц; gone — больше месяца назад; never — ни разу.
 */
export function presenceOf(iso: string | null | undefined, now: number = Date.now()): Presence {
  if (!iso) return 'never';
  const diff = now - new Date(iso).getTime();
  if (diff < 15 * MINUTE) return 'online';
  if (diff < 7 * DAY) return 'recent';
  if (diff < 30 * DAY) return 'idle';
  return 'gone';
}

export const PRESENCE_COLOR: Record<Presence, string> = {
  online: 'var(--color-success)',
  recent: 'var(--color-success)',
  idle: 'var(--color-warning)',
  gone: 'var(--color-text-secondary)',
  never: 'var(--color-border)',
};

export const PRESENCE_LABEL: Record<Presence, string> = {
  online: 'Сейчас в Lens',
  recent: 'Заходы на этой неделе',
  idle: 'Заходы в этом месяце',
  gone: 'Больше месяца без заходов',
  never: 'Ни одного захода',
};

/** Браузер и система из User-Agent — коротко, для истории входов. */
export function describeUserAgent(ua: string | null | undefined): string {
  if (!ua) return 'неизвестно';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /YaBrowser\//.test(ua)
      ? 'Яндекс Браузер'
      : /OPR\//.test(ua)
        ? 'Opera'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : /Chrome\//.test(ua)
            ? 'Chrome'
            : /Safari\//.test(ua)
              ? 'Safari'
              : /curl|node|python|axios|undici/i.test(ua)
                ? 'Скрипт'
                : 'Браузер';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac OS X|Macintosh/.test(ua)
        ? 'macOS'
        : /Android/.test(ua)
          ? 'Android'
          : /Linux/.test(ua)
            ? 'Linux'
            : null;
  return os ? `${browser} · ${os}` : browser;
}
